import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';
import axios from 'axios';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// --- Utilities ---
const buildModifierSignature = (modifiers) => {
  if (!Array.isArray(modifiers) || modifiers.length === 0) return 'none';
  const parts = modifiers
    .filter(Boolean)
    .map((mod) => {
      if (mod.id != null) return String(mod.id);
      const group = mod.group_name || '';
      const name = mod.name || '';
      return `${group}:${name}`;
    })
    .sort();
  return parts.join('|');
};

const generateItemSignature = (productId, modifiers) => {
  return `${productId}-${buildModifierSignature(modifiers)}`;
};

const hasTravelBookingPayload = (extra) => {
  return !!(
    extra &&
    typeof extra === 'object' &&
    extra.travel_booking &&
    typeof extra.travel_booking === 'object'
  );
};

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState([]);
  const [deliveryLocation, setDeliveryLocation] = useState(null); // { address, coords }
  const [deliveryQuotes, setDeliveryQuotes] = useState({}); // storeId -> fee
  const [isQuoting, setIsQuoting] = useState(false);

  const cartStorageKey = useMemo(() => {
    const userPart = user?.id ? String(user.id) : 'anon';
    return `mx_global_store_cart_v1:${userPart}`;
  }, [user?.id]);

  // Grouped items by store for easy rendering
  const itemsByStore = useMemo(() => {
    const groups = {};
    cart.forEach((item) => {
      const storeId = item.store_id || 'unknown';
      if (!groups[storeId]) {
        groups[storeId] = {
          store_id: storeId,
          store_slug: item.store_slug,
          store_name: item.store_name,
          items: [],
          subtotal: 0,
        };
      }
      groups[storeId].items.push(item);
      groups[storeId].subtotal += item.totalPrice;
    });
    return Object.values(groups);
  }, [cart]);

  // Dynamic Delivery Quoting
  const refreshDeliveryQuotes = useCallback(async (location, stores) => {
    if (!location || !stores?.length) {
      setDeliveryQuotes({});
      return;
    }

    setIsQuoting(true);
    const newQuotes = {};
    
    try {
      await Promise.all(stores.map(async (store) => {
        try {
          const response = await axios.post('/api/store/order/delivery-quote', {
            store_id: store.store_id,
            subtotal_usdt: store.subtotal,
            delivery_info: {
              address: location.address,
              coords: location.coords
            }
          });
          newQuotes[store.store_id] = response.data.fee_usdt;
        } catch (err) {
          console.error(`[Cart] Error quoting delivery for store ${store.store_id}:`, err);
          newQuotes[store.store_id] = null; // Mark as error/unavailable
        }
      }));
      setDeliveryQuotes(newQuotes);
    } finally {
      setIsQuoting(false);
    }
  }, []);

  // Sync delivery quotes when location or stores change
  useEffect(() => {
    refreshDeliveryQuotes(deliveryLocation, itemsByStore);
  }, [deliveryLocation, itemsByStore, refreshDeliveryQuotes]);

  // Load cart on mount or user change
  useEffect(() => {
    try {
      const raw = localStorage.getItem(cartStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setCart(parsed);
        }
      } else {
        setCart([]);
      }
    } catch (err) {
      console.error('[CartContext] Error loading cart:', err);
      setCart([]);
    }
  }, [cartStorageKey]);

  // Save cart on changes
  useEffect(() => {
    if (!cartStorageKey) return;
    try {
      localStorage.setItem(cartStorageKey, JSON.stringify(cart));
    } catch (err) {
      console.error('[CartContext] Error saving cart:', err);
    }
  }, [cart, cartStorageKey]);

  const addToCart = (product, quantity, modifiers = [], storeData = {}, doc_forms = {}, appointment = null, extra = null) => {
    // Unique signature includes modifiers AND specific attachments if they prevent merging
    const hasAttachments =
      (doc_forms && Object.keys(doc_forms).length > 0) ||
      (appointment && appointment.hold_id) ||
      hasTravelBookingPayload(extra);
    const attachmentPart = hasAttachments ? `-${Math.random().toString(36).substring(2, 7)}` : '';
    const signature = generateItemSignature(product.id, modifiers) + attachmentPart;
    
    setCart((prevCart) => {
      const existingIndex = prevCart.findIndex((item) => item.signature === signature);
      
      const itemPrice = Number(product.price || product.price_usdt || 0);
      const modifiersPrice = modifiers.reduce((acc, m) => {
        const extraRaw = m.price_adjustment_usdt != null ? Number(m.price_adjustment_usdt) : (m.price_adjustment != null ? Number(m.price_adjustment) : 0);
        return acc + extraRaw;
      }, 0);
      const unitPrice = itemPrice + modifiersPrice;

      if (existingIndex > -1 && !hasAttachments) {
        const newCart = [...prevCart];
        const newQty = newCart[existingIndex].quantity + quantity;
        newCart[existingIndex] = {
          ...newCart[existingIndex],
          quantity: newQty,
          totalPrice: newQty * unitPrice,
        };
        toast.success(`Actualizado: ${product.name}`);
        return newCart;
      } else {
        const newItem = {
          signature,
          product, // Store full product object for legacy compatibility
          product_id: product.id,
          name: product.name,
          image_url: product.image_url,
          image_thumb_url: product.image_thumb_url,
          price: itemPrice,
          unitPrice,
          quantity,
          modifiers,
          doc_forms,
          appointment,
          extra,
          totalPrice: quantity * unitPrice,
          currency: product.currency || 'USD',
          store_id: storeData.id || product.store_id || storeData.store_id,
          store_slug: storeData.slug || product.store_slug || storeData.store_slug,
          store_name: storeData.name || product.store_name || storeData.store_name,
          store_display_currencies: storeData.settings?.catalog_display_currencies || 
                                   (storeData.settings?.catalog_display_currency ? [storeData.settings.catalog_display_currency] : ['usdt']),
          added_at: new Date().toISOString(),
        };
        toast.success(`Agregado: ${product.name}`);
        return [...prevCart, newItem];
      }
    });
  };

  const removeFromCart = (signature) => {
    setCart((prev) => prev.filter((item) => item.signature !== signature));
  };

  const updateQuantity = (signature, delta) => {
    setCart((prev) => {
      const index = prev.findIndex((item) => item.signature === signature);
      if (index === -1) return prev;

      const newCart = [...prev];
      const newQty = Math.max(0, newCart[index].quantity + delta);

      if (newQty === 0) {
        return newCart.filter((_, i) => i !== index);
      }

      newCart[index] = {
        ...newCart[index],
        quantity: newQty,
        totalPrice: newQty * newCart[index].unitPrice,
      };
      return newCart;
    });
  };

  const clearCart = () => {
    setCart([]);
  };

  const clearStoreItems = (storeId) => {
    setCart((prev) => prev.filter((item) => String(item.store_id) !== String(storeId)));
  };

  const globalTotal = useMemo(() => {
    const itemsTotal = cart.reduce((acc, item) => acc + item.totalPrice, 0);
    const deliveryTotal = Object.values(deliveryQuotes).reduce((acc, fee) => acc + (Number(fee) || 0), 0);
    return itemsTotal + deliveryTotal;
  }, [cart, deliveryQuotes]);

  const totalItems = useMemo(() => {
    return cart.reduce((acc, item) => acc + item.quantity, 0);
  }, [cart]);

  const value = {
    cart,
    itemsByStore,
    totalItems,
    globalTotal,
    deliveryLocation,
    deliveryQuotes,
    isQuoting,
    setDeliveryLocation,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    clearStoreItems,
    setCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
